<script lang="ts">
  import FeatureFlag from '@/platform-redesign-components/FeatureFlag.svelte';
  import {step, thumbnail, ThumbnailState, thumbTemplates} from '@/store';
  import disabled from '@/store/disabled';
  import type {GridConfig, PreviewImage} from '@/types/common';
  import {STEP} from '@/types/common';
  import type {Shapes, Tags} from '@/types/filters';
  import type {Template} from '@/types/project';
  import {topnav} from '@/utils/top-nav-next-custom-events';
  import {Container, TopNav} from '@components';
  import {onDestroy, onMount} from 'svelte';
  import Swal from 'sweetalert2';
  import StatsService from '../../services/stats';
  import TemplatesGrid from './TemplatesGrid.svelte';
  import TemplatesSidebar from './TemplatesSidebar.svelte';

  //let disabled: boolean = true;
  let hasTemplateSelected = false;
  let selectedTemplate: PreviewImage;

  let filterShape: Shapes = '';
  let filterTags: Tags[] = [];
  let filterFavorites: boolean = false;

  let gridProps: GridConfig = {
    images: [],
    loading: true,
    columnCount: 4
  };

  let page: number = 1;
  let limit: number = 16;
  let favorites: boolean = false;
  let selectedTags: string = '';
  let selectedShape: Shapes = '';

  let loadMore: Boolean = false;

  const statsService = new StatsService();

  const addGridProps = (filtered: Template[]) => {
    const images = filtered.map(template => {
      let obj: PreviewImage = {
        id: template.id,
        src: template.preview,
        content: template.content,
        title: template.title,
        isFeatured: template.isFeatured,
        isFavorite: template.isFavorite,
        isFree: template.isFree,
        devalueLevel: template.devalueLevel
      };

      return obj;
    });

    gridProps = {
      ...gridProps,
      images: images
    };
  };

  const unsubscribeToTemplate = thumbTemplates.subscribe(t => {
    addGridProps(t.items);
  });

  const unsubscribeToProject = thumbnail.subscribe(p => {
    if (p.template !== null) {
      $disabled = false;
      selectedTemplate = {
        hasHover: false,
        id: p.template.id,
        content: p.template.content,
        src: p.template.preview,
        title: p.template.title,
        devalueLevel: p.template.devalueLevel
      };
    }
  });

  const filter = async (shape: Shapes, tags: Tags[], favorite: boolean) => {
    page = 1;
    let changed = false;

    if (favorite != favorites) {
      changed = true;
      favorites = favorite;
    }

    if (tags.length == 0 && selectedTags != '') {
      changed = true;
    }

    if (shape == 'All') {
      selectedShape = '';
    } else {
      selectedShape = shape;
    }

    if (tags.length >= 1) {
      selectedTags = tags.join(',');
    } else {
      selectedTags = '';
    }

    if (selectedTags.length != 0 || changed == true || selectedShape != '' || selectedTags != '' || shape == 'All') {
      gridProps.loading = true;
      gridProps = gridProps;
      thumbTemplates.set({items: []});

      await thumbTemplates
        .getTemplates(page, limit, selectedShape, selectedTags, favorites, true, $thumbnail.type)
        .then(() => {
          gridProps.loading = false;
          gridProps = gridProps;
        });
    }
  };

  $: filter(filterShape, filterTags, filterFavorites);

  const onTemplateSelect = async (e: CustomEvent) => {
    if (e.detail.isFree == false) return;

    $disabled = e.detail.disabled;
    hasTemplateSelected = false;

    if (e.detail.disabled) return;

    const template = (await thumbTemplates.getTemplate(e.detail.image.id)) as Template;

    if (!template == null) {
      disabled.set(true);
      hasTemplateSelected = false;
      return;
    }

    topnav.dispatchEvent({nextDisabled: false, nextDisabledTip: ''});
    disabled.set(false);
    hasTemplateSelected = true;
    thumbnail.selectTemplate(template);
    window.dispatchEvent(new Event('reset-timeline'));
  };

  const onAppendTemplates = async (e: CustomEvent) => {
    if (page === 1) return;
    thumbTemplates.getTemplates(
      e.detail.currentPage,
      limit,
      selectedShape,
      selectedTags,
      favorites,
      false,
      $thumbnail.type
    );
  };

  $: onVideoChange($thumbnail);

  const onVideoChange = (thumbnail: ThumbnailState) => {
    if (thumbnail.video === null) return;
    $disabled = !(thumbnail.video.transcription && thumbnail.video.transcription.sentences.length > 0);
  };

  const proceedToNextStepCheck = () => {
    if ($thumbnail.template) {
      // statsService.saveTemplateData($thumbnail.template.id);
      step.update(STEP.PREVIEW);
    } else {
      openSwal();
    }
  };
  const updatePrevStep = () => step.update(STEP.CHOOSE_VIDEO);
  const updateNextStep = () => proceedToNextStepCheck();
  const openSwal = () => {
    let text = '';
    if (!hasTemplateSelected) {
      text = 'You have not selected a template yet. Choose 1 to continue.';
    } else if ($thumbnail.video?.transcription == null || $thumbnail.video?.transcription.sentences.length <= 0) {
      text = 'Please wait for the video to finish transcribing.';
    } else if ($thumbnail.video?.transcription.transcription !== null) {
      text = 'There was a problem with the transcription... please contact support.';
    }

    Swal.fire({
      titleText: 'Warning',
      text,
      icon: 'warning'
    });
  };

  const checkScrollEvent = (e: Event) => {
    const target = e.target as Element;
    const {scrollHeight, scrollTop, clientHeight} = target;

    loadMore = Math.abs(scrollHeight - scrollTop - 2) <= Math.abs(clientHeight);
  };

  onMount(() => {
    thumbTemplates
      .getTemplates(page, limit, selectedShape, selectedTags, favorites, false, $thumbnail.type)
      .then(() => {
        gridProps.loading = false;
        gridProps = gridProps;
      });

    if ($thumbnail.template) {
      topnav.dispatchEvent({nextDisabled: false, nextDisabledTip: ''});
    } else {
      topnav.dispatchEvent({nextDisabled: true, nextDisabledTip: 'You must select a template to proceed'});
    }

    topnav.disabledNext(openSwal);
    topnav.next(updateNextStep);
    topnav.previous(updatePrevStep);

    onTemplateSelect({
      detail: {
        image: {id: $thumbnail?.template?.id, isFree: true}
      }
    } as any);
  });

  onDestroy(() => {
    unsubscribeToTemplate();
    unsubscribeToProject();
  });
</script>

<div id="choose_template" />

<FeatureFlag>
  <div slot="current_component">
    <TopNav
      step={2}
      disabled={$disabled}
      hasPrevious={true}
      on:disabled={openSwal}
      on:next={updateNextStep}
      on:prev={updatePrevStep}
      label="Choose a template"
    />
    <Container className="flex overflow-y-hidden h-full no-scrollbar">
      <TemplatesSidebar
        bind:shape={filterShape}
        bind:favorites={filterFavorites}
        bind:currentTags={filterTags}
        on:change-video={updatePrevStep}
        selected={selectedTemplate}
      />
      <div class="relative flex w-full flex-grow flex-col overflow-auto" on:scroll={checkScrollEvent}>
        <div class="hidden w-full items-center bg-gray-100 p-8">
          <div class="flex flex-grow flex-col">
            <div>
              <h1 class="!p-0 text-4xl font-bold !leading-none">Choose a Template</h1>
            </div>
            <div>
              <p class="!p-0 !leading-none">
                Pick the template that best fits your need. You'll be able to update colors, fonts, logo, etc. in STEP
                4.
              </p>
            </div>
          </div>
        </div>

        <TemplatesGrid
          {loadMore}
          props={gridProps}
          selected={selectedTemplate}
          bind:page
          bind:limit
          bind:selectedTags
          bind:selectedShape
          bind:favorites
          on:on-image-click={onTemplateSelect}
          on:append-templates={onAppendTemplates}
        />
      </div>
    </Container>
  </div>
  <div class="flex flex-col" slot="new_component">
    <Container className="flex overflow-y-hidden h-full no-scrollbar">
      <TemplatesSidebar
        bind:shape={filterShape}
        bind:favorites={filterFavorites}
        bind:currentTags={filterTags}
        on:change-video={updatePrevStep}
        selected={selectedTemplate}
      />
      <div class="relative flex w-full flex-grow flex-col overflow-auto" on:scroll={checkScrollEvent}>
        <div class="hidden w-full items-center bg-gray-100 p-8">
          <div class="flex flex-grow flex-col">
            <div>
              <h1 class="!p-0 text-4xl font-bold !leading-none">Choose a Template</h1>
            </div>
            <div>
              <p class="!p-0 !leading-none">
                Pick the template that best fits your need. You'll be able to update colors, fonts, logo, etc. in STEP
                4.
              </p>
            </div>
          </div>
        </div>

        <TemplatesGrid
          {loadMore}
          props={gridProps}
          selected={selectedTemplate}
          bind:page
          bind:limit
          bind:selectedTags
          bind:selectedShape
          bind:favorites
          on:on-image-click={onTemplateSelect}
          on:append-templates={onAppendTemplates}
        />
      </div>
    </Container>
  </div>
</FeatureFlag>

<style lang="postcss">
  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }

  ::-webkit-scrollbar {
    @apply w-6 rounded-full bg-slate-100 p-1;
  }
  ::-webkit-scrollbar-track {
    @apply rounded-full shadow-gray-500;
  }
  ::-webkit-scrollbar-thumb {
    @apply rounded-full bg-gray-400;
  }
  ::-webkit-scrollbar-thumb:hover {
    @apply rounded-full bg-gray-500;
  }
</style>
